import { LEVEL_WIDTH, TileData, TileType } from "./types";
import { tileKey, type EnemySpawn, type LevelData } from "./level";

// World 3-3: Floating platforms, many gaps, tricky jumps
export function buildLevel3_3(): LevelData {
  const tiles = new Map<string, TileData>();
  const groundTop = -1;
  const groundDepth = 2;

  const pits: Array<[number, number]> = [
    [16, 19],
    [34, 37],
    [52, 55],
    [68, 71],
    [84, 87],
    [100, 103],
    [116, 119],
    [132, 135],
    [148, 151],
    [164, 167],
    [180, 183]
  ];

  const setTile = (x: number, y: number, type: TileType, payload?: TileData["payload"], flower?: boolean): void => {
    tiles.set(tileKey(x, y), { type, payload, broken: false, bump: 0, flower: flower || undefined });
  };

  const isPit = (x: number): boolean => pits.some(([a, b]) => x >= a && x <= b);

  for (let x = 0; x < LEVEL_WIDTH; x++) {
    if (isPit(x)) continue;
    for (let dy = 0; dy < groundDepth; dy++) {
      setTile(x, groundTop - dy, TileType.Ground);
    }
  }

  const stair = (x: number, h: number): void => {
    for (let dy = 0; dy < h; dy++) setTile(x, groundTop + 1 + dy, TileType.Block);
  };

  // floating platform section 1
  for (let x = 21; x <= 26; x++) setTile(x, 4, TileType.Brick);
  setTile(23, 4, TileType.Question, { kind: "mushroom" });
  setTile(25, 4, TileType.Question, { kind: "coin" });

  // floating platform section 2
  for (let x = 41; x <= 46; x++) setTile(x, 4, TileType.Brick);
  setTile(43, 4, TileType.Question, { kind: "coin" });
  setTile(45, 4, TileType.Question, { kind: "mushroom" });

  // floating platform section 3
  for (let x = 57; x <= 62; x++) setTile(x, 4, TileType.Brick);
  setTile(59, 4, TileType.Question, { kind: "coin" });

  // floating platform section 4
  for (let x = 73; x <= 78; x++) setTile(x, 4, TileType.Brick);
  setTile(75, 4, TileType.Question, { kind: "coin" });
  setTile(77, 4, TileType.Question, { kind: "mushroom" });

  // floating platform section 5
  for (let x = 89; x <= 94; x++) setTile(x, 4, TileType.Brick);
  setTile(91, 4, TileType.Question, { kind: "coin" });

  // floating platform section 6
  for (let x = 105; x <= 110; x++) setTile(x, 4, TileType.Brick);
  setTile(107, 4, TileType.Question, { kind: "mushroom" });
  setTile(109, 4, TileType.Question, { kind: "coin" });

  // floating platform section 7
  for (let x = 121; x <= 126; x++) setTile(x, 4, TileType.Brick);
  setTile(123, 4, TileType.Question, { kind: "coin" });

  // floating platform section 8
  for (let x = 137; x <= 142; x++) setTile(x, 4, TileType.Brick);
  setTile(139, 4, TileType.Question, { kind: "coin" });
  setTile(141, 4, TileType.Question, { kind: "mushroom" });

  // floating platform section 9
  for (let x = 153; x <= 158; x++) setTile(x, 4, TileType.Brick);
  setTile(155, 4, TileType.Question, { kind: "coin" });

  // floating platform section 10
  for (let x = 169; x <= 174; x++) setTile(x, 4, TileType.Brick);
  setTile(171, 4, TileType.Question, { kind: "coin" });
  setTile(173, 4, TileType.Question, { kind: "mushroom" });

  // pipes
  const pipes: Array<[number, number]> = [
    [29, 2],
    [49, 3],
    [66, 2],
    [82, 3],
    [98, 2],
    [114, 3],
    [130, 2],
    [146, 3],
    [162, 2],
    [178, 3]
  ];
  for (const [px, h] of pipes) {
    for (let dy = 0; dy < h; dy++) {
      setTile(px, groundTop + 1 + dy, TileType.Pipe);
      setTile(px + 1, groundTop + 1 + dy, TileType.Pipe);
    }
  }

  // high coins
  setTile(27, 7, TileType.Question, { kind: "coin" });
  setTile(47, 7, TileType.Question, { kind: "coin" });
  setTile(63, 7, TileType.Question, { kind: "coin" });
  setTile(79, 7, TileType.Question, { kind: "coin" });
  setTile(95, 7, TileType.Question, { kind: "coin" });
  setTile(111, 7, TileType.Question, { kind: "coin" });
  setTile(127, 7, TileType.Question, { kind: "coin" });
  setTile(143, 7, TileType.Question, { kind: "coin" });
  setTile(159, 7, TileType.Question, { kind: "coin" });
  setTile(175, 7, TileType.Question, { kind: "coin" });

  // final staircase
  stair(184, 1);
  stair(185, 2);
  stair(186, 3);
  stair(187, 4);
  stair(188, 4);
  stair(189, 4);
  stair(190, 3);
  stair(191, 2);
  stair(192, 1);

  const flagX = 198;
  for (let y = groundTop + 1; y <= 21; y++) setTile(flagX, y, TileType.Pole);
  setTile(flagX, 22, TileType.Block);

  const castleX = 202;

  const enemies: EnemySpawn[] = [
    { kind: "goomba", x: 22 },
    { kind: "koopa", x: 28 },
    { kind: "goomba", x: 42 },
    { kind: "goomba", x: 48 },
    { kind: "koopa", x: 58 },
    { kind: "goomba", x: 74 },
    { kind: "goomba", x: 80 },
    { kind: "koopa", x: 90 },
    { kind: "goomba", x: 106 },
    { kind: "goomba", x: 112 },
    { kind: "koopa", x: 122 },
    { kind: "goomba", x: 138 },
    { kind: "goomba", x: 144 },
    { kind: "koopa", x: 154 },
    { kind: "goomba", x: 170 },
    { kind: "goomba", x: 176 }
  ];

  return { tiles, enemies, enterablePipes: [], flagX, castleX, width: LEVEL_WIDTH, groundY: 0 };
}
